import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import {
  Activity,
  BarChart3,
  Calendar,
  Coins,
  Download,
  PieChart,
  TrendingUp,
  Users,
} from 'lucide-react';
import ReactApexChart from 'react-apexcharts';
import { toast } from 'sonner';
import { WalletAnalytics, walletService } from '@/services/wallet.service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type DateRangeOption = '7' | '30' | '90';

/**
 * Wallet Statistics Dashboard
 * Comprehensive metrics and visualizations for wallet ecosystem
 */
export function WalletStatisticsPage() {
  const [dateRange, setDateRange] = useState<DateRangeOption>('30');

  // Calculate date range
  const getDateRange = () => {
    const days = parseInt(dateRange);
    const to = new Date();
    const from = subDays(to, days);
    return {
      from: format(from, "yyyy-MM-dd'T'00:00:00"),
      to: format(to, "yyyy-MM-dd'T'23:59:59"),
    };
  };

  // Fetch wallet analytics
  const { data: analytics, isLoading } = useQuery<WalletAnalytics>({
    queryKey: ['wallet-statistics', dateRange],
    queryFn: () => walletService.getWalletAnalytics(getDateRange()),
    refetchInterval: 120000, // Refresh every 2 minutes
  });

  // Export statistics report
  const handleExport = () => {
    if (!analytics) return;

    const csvContent = [
      ['Wallet Statistics Report'],
      ['Generated:', new Date().toISOString()],
      ['Date Range:', `Last ${dateRange} days`],
      [''],
      ['Summary'],
      [
        'Total Coins in Circulation',
        analytics.totalCoinsInCirculation.toLocaleString(),
      ],
      ['Distributed Today', analytics.coinsDistributedToday.toLocaleString()],
      [
        'Distributed This Week',
        analytics.coinsDistributedThisWeek.toLocaleString(),
      ],
      [
        'Distributed This Month',
        analytics.coinsDistributedThisMonth.toLocaleString(),
      ],
      [''],
      ['Top 10 Wallets'],
      ['Rank', 'User', 'Balance'],
      ...analytics.topWallets.map((wallet, idx) => [
        (idx + 1).toString(),
        wallet.userName,
        wallet.balance.toLocaleString(),
      ]),
      [''],
      ['Transaction Volume'],
      ['Date', 'Volume (Coins)', 'Transaction Count'],
      ...analytics.transactionVolumeChart.map((point) => [
        point.date,
        point.volume.toLocaleString(),
        point.transactions.toLocaleString(),
      ]),
      [''],
      ['Distribution by Segment'],
      ['Segment', 'Total Coins', 'User Count', 'Avg per User'],
      ...analytics.distributionBySegment.map((segment) => [
        segment.segment,
        segment.totalCoins.toLocaleString(),
        segment.userCount.toLocaleString(),
        (segment.totalCoins / segment.userCount).toFixed(2),
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `wallet-statistics-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success('Statistics report exported');
  };

  // Transaction volume chart configuration
  const volumeChartOptions = {
    series: [
      {
        name: 'Coin Volume',
        data: analytics?.transactionVolumeChart.map((p) => p.volume) || [],
        color: '#3b82f6',
      },
      {
        name: 'Transactions',
        data:
          analytics?.transactionVolumeChart.map((p) => p.transactions) || [],
        color: '#10b981',
      },
    ],
    options: {
      chart: {
        type: 'bar' as const,
        height: 350,
        toolbar: { show: true },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '70%',
        },
      },
      dataLabels: { enabled: false },
      stroke: { show: true, width: 2, colors: ['transparent'] },
      xaxis: {
        categories:
          analytics?.transactionVolumeChart.map((p) =>
            format(new Date(p.date), 'MMM dd'),
          ) || [],
        title: { text: 'Date' },
      },
      yaxis: [
        {
          title: { text: 'Coin Volume' },
          labels: {
            formatter: (value: number) => value.toLocaleString(),
          },
        },
        {
          opposite: true,
          title: { text: 'Transaction Count' },
          labels: {
            formatter: (value: number) => Math.floor(value).toLocaleString(),
          },
        },
      ],
      fill: { opacity: 1 },
      tooltip: {
        y: {
          formatter: (value: number) => value.toLocaleString(),
        },
      },
      legend: {
        position: 'top' as const,
        horizontalAlign: 'right' as const,
      },
    },
  };

  // Distribution by segment chart configuration
  const segmentChartOptions = {
    series: analytics?.distributionBySegment.map((s) => s.totalCoins) || [],
    options: {
      chart: {
        type: 'donut' as const,
        height: 350,
      },
      labels: analytics?.distributionBySegment.map((s) => s.segment) || [],
      colors: [
        '#3b82f6',
        '#10b981',
        '#f59e0b',
        '#ef4444',
        '#8b5cf6',
        '#ec4899',
      ],
      legend: {
        position: 'bottom' as const,
      },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total Coins',
                formatter: () =>
                  analytics?.totalCoinsInCirculation.toLocaleString() || '0',
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val: number) => `${val.toFixed(1)}%`,
      },
      tooltip: {
        y: {
          formatter: (value: number) => `${value.toLocaleString()} coins`,
        },
      },
    },
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[600px]">
        <div className="text-center">
          <Activity className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading statistics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Wallet Statistics</h1>
          <p className="text-muted-foreground mt-1">
            Comprehensive wallet ecosystem metrics and insights
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={dateRange}
            onValueChange={(value) => setDateRange(value as DateRangeOption)}
          >
            <SelectTrigger className="w-[180px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleExport} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total in Circulation
            </CardTitle>
            <Coins className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.totalCoinsInCirculation.toLocaleString() || '0'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              All user wallets combined
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Distributed Today
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.coinsDistributedToday.toLocaleString() || '0'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              New coins distributed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
            <BarChart3 className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.coinsDistributedThisWeek.toLocaleString() || '0'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              7-day distribution
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <Calendar className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.coinsDistributedThisMonth.toLocaleString() || '0'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              30-day distribution
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Transaction Volume Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Transaction Volume
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics && analytics.transactionVolumeChart.length > 0 ? (
              <ReactApexChart
                options={volumeChartOptions.options}
                series={volumeChartOptions.series}
                type="bar"
                height={350}
              />
            ) : (
              <div className="flex items-center justify-center h-[350px] text-muted-foreground">
                No transaction data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Distribution by Segment Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Distribution by Segment
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics && analytics.distributionBySegment.length > 0 ? (
              <ReactApexChart
                options={segmentChartOptions.options}
                series={segmentChartOptions.series}
                type="donut"
                height={350}
              />
            ) : (
              <div className="flex items-center justify-center h-[350px] text-muted-foreground">
                No segment data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Wallets Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Top 10 Wallets
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analytics?.topWallets.slice(0, 10).map((wallet, index) => {
              const totalCoins = analytics.totalCoinsInCirculation;
              const percentage =
                totalCoins > 0 ? (wallet.balance / totalCoins) * 100 : 0;

              return (
                <div
                  key={wallet.userId}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                        index === 0
                          ? 'bg-yellow-500 text-white'
                          : index === 1
                            ? 'bg-gray-400 text-white'
                            : index === 2
                              ? 'bg-orange-600 text-white'
                              : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{wallet.userName}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 bg-muted rounded-full h-2 max-w-xs">
                          <div
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {percentage.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Coins className="h-4 w-4 text-warning" />
                    <span className="font-bold text-lg">
                      {wallet.balance.toLocaleString()}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Distribution by Segment Details */}
      <Card>
        <CardHeader>
          <CardTitle>Segment Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analytics?.distributionBySegment.map((segment, index) => {
              const avgPerUser =
                segment.userCount > 0
                  ? segment.totalCoins / segment.userCount
                  : 0;
              const totalCoins = analytics.totalCoinsInCirculation;
              const percentage =
                totalCoins > 0 ? (segment.totalCoins / totalCoins) * 100 : 0;

              return (
                <div key={segment.segment} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="text-sm">
                        {segment.segment}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {segment.userCount.toLocaleString()} users
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">
                        {segment.totalCoins.toLocaleString()} coins
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {percentage.toFixed(1)}% of total
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="bg-muted rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            index === 0
                              ? 'bg-blue-500'
                              : index === 1
                                ? 'bg-green-500'
                                : index === 2
                                  ? 'bg-amber-500'
                                  : 'bg-gray-500'
                          }`}
                          style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground whitespace-nowrap">
                      Avg: {avgPerUser.toFixed(0)} coins/user
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
